/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { EmailOrUsernameAuthFlowForm } from "@packages/sdk";
import {
  emailOrUsernameAuthFlowSchema,
  useRequestAuth,
  useRequestOtp,
} from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { spacing } from "../../../../global/emotion";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { standardSchemaResolver } from "../../../reactHookForm";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  [cssMediaQueryMinWidthTablet]: { justifyContent: "flex-end" },
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};
export const AuthEmailOrUsernameFlow = ({
  setFlow,
  email,
  state,
  setState,
  customSchema,
  layoutCSS = {},
  hideHeader,
  className = "",
}: AuthFlowProps) => {
  const t = useTranslations();
  const alert = useAlert();
  const { mutationEmailExists } = useRequestAuth();
  const { mutationPost: sendOtp } = useRequestOtp();
  const { track } = useAnalytics();

  useViewedScreen({ screen: "email_or_username_input" });

  const onSubmit = async ({ login }: EmailOrUsernameAuthFlowForm) => {
    let newState: typeof state = state;
    try {
      const isEmail = z.email().safeParse(login)?.success;

      if (isEmail) {
        track({
          event: HallowAnalyticsEvent.EnteredEmail,
          properties: { screen_name: "email_or_username_input" },
        });
      } else {
        track({
          event: HallowAnalyticsEvent.EnteredUsername,
          properties: { screen_name: "email_or_username_input" },
        });
      }

      const exists = isEmail
        ? await mutationEmailExists.mutateAsync({ email: login })
        : { exists: true, use_magic_link_flow: false };
      newState = { ...state, login, ...exists };

      /** MAGIC */
      if (exists.exists && exists.use_magic_link_flow) {
        await sendOtp.mutateAsync({ email: login });
        newState = { ...state, phone: undefined, email: login, ...exists };
        setFlow("otp");
      } else setFlow("studentPassword");
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    } finally {
      setState(newState);
    }
  };
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<EmailOrUsernameAuthFlowForm>(
      standardSchemaResolver(customSchema ?? emailOrUsernameAuthFlowSchema),
    );
  const form = useForm({ resolver });

  return (
    <AuthForm<EmailOrUsernameAuthFlowForm>
      form={form}
      defaultValues={{ login: email }}
      onSubmit={onSubmit}
      className={className}
      layoutCSS={layoutCSS}
      footer={(_, disabled) => (
        <div key={"buttons"} css={[buttonsStyle, justOneStyle]}>
          <AuthSubmitButton
            disabled={disabled}
            data-testid={HallowDataTestId.AuthContinueButton}
          >
            {t("onboarding_button_continue")}
          </AuthSubmitButton>
        </div>
      )}
    >
      {(form) => (
        <>
          {!hideHeader && (
            <EmailHeader
              onBack={() => {
                track({
                  event: HallowAnalyticsEvent.TappedBack,
                  properties: { screen_name: "email_or_username_input" },
                });
                setFlow("student");
              }}
            />
          )}
          <Text type="headline" size="m" as="h2">
            {t("email_username_input_headline")}
          </Text>
          <FormField
            control={form.control}
            name="login"
            showErrorMessage={false}
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthTextInput
                  type="text"
                  required
                  autoFocus
                  autoComplete="username"
                  autoCorrect="off"
                  placeholder={t("email_username_input_hint")}
                  css={fieldsCSS[field.name]}
                  {...inputProps}
                  {...field}
                />
                <AuthError css={errorsCSS[field.name]}>
                  {errors?.[field.name]?.message
                    ? t(errors[field.name].message)
                    : ""}
                </AuthError>
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};
